/**
 * 객체 또는 배열 깊은 복사(deep copy)
 * - fallback 방식은 함수, `Date`, `Map`, `Set`, 순환 참조가 있는 객체 등은 올바르게 복제되지 않습니다.
 * @param value - 복사할 원본 값 (객체, 배열 등)
 * @returns 복사된 값
 */
export const deepCopy = <T>(value: T): T => {
  // 최신 브라우저
  if (typeof structuredClone === 'function') {
    return structuredClone(value)
  }

  // 구형 브라우저
  return JSON.parse(JSON.stringify(value))
}

/**
 * 객체의 모든 키를 정렬한 새 객체를 반환
 * @param obj - 정렬할 대상 객체 또는 배열
 * @returns 키가 정렬된 객체 또는 배열
 */
const sortKeys = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(sortKeys)
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortKeys(obj[key])
        return acc
      }, {} as any)
  }
  return obj
}

/**
 * 두 객체가 구조적으로 동일한지 비교하는 함수
 * sortKeys를 통해 키 순서를 정렬한 후 문자열 비교로 정확도를 높임
 * @param a - 비교할 첫 번째 객체
 * @param b - 비교할 두 번째 객체
 * @returns 두 객체가 동일한 구조와 값을 가지면 true, 아니면 false
 */
export const isObjectEqual = <T>(a: T, b: T): boolean => {
  const normalize = (obj: any) =>
    JSON.stringify(sortKeys(obj))

  return normalize(a) === normalize(b)
}

/**
 * 객체 데이터를 기반으로 query string 생성
 * @param data - 쿼리 파라미터로 사용할 키-값 객체
 * @returns URL 인코딩된 query string (예: 'simType=esim&simUsageType=extend')
 */
export const makeQueryString = (data: Record<string, string|number|boolean>): string => {
  const queryParams = new URLSearchParams()

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value))
    }
  })

  return queryParams.toString()
}

/**
 * 쿼리 문자열을 객체로 변환하며, string 외 boolean/number 타입도 자동 변환
 * @param query - 'key=value&key2=value2' 형식의 쿼리 문자열
 * @returns 키-값 쌍의 객체 (예: { simType: 'esim', isGuest: 'false' })
 */
export const parseQueryString = (
  query: string
): Record<string, string | number | boolean> => {
  const params = new URLSearchParams(query)
  const result: Record<string, string | number | boolean> = {}

  for (const [key, value] of params.entries()) {
    // boolean 타입 처리
    if (value === 'true') {
      result[key] = true
    } else if (value === 'false') {
      result[key] = false
    }
    // number 타입 처리 (단, NaN이 아닌 경우만)
    else if (!isNaN(Number(value)) && value.trim() !== '') {
      result[key] = Number(value)
    }
    // 기본 string
    else {
      result[key] = value
    }
  }

  return result
}

